import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const filterByStatus = (chatStatus, filterStatus) => {
  if (filterStatus === 'all') return true;
  if (filterStatus === 'unresolved') return chatStatus !== 'resolved';
  return chatStatus === filterStatus;
};

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};

export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const getEffectiveConversationStatus = conversation =>
  conversation?.thread_aggregate_status || conversation?.status;

export const getEffectiveUnreadCount = conversation =>
  conversation?.thread_unread_count ?? conversation?.unread_count ?? 0;

export const getOperationalConversationId = conversation =>
  conversation?.active_conversation_id || conversation?.id;

export const getConversationSortActivityAt = conversation =>
  conversation?.thread_last_activity_at ??
  conversation?.last_activity_at ??
  conversation?.timestamp ??
  0;

export const findListRowIndexByThread = (
  allConversations,
  { conversationId, contactInboxId } = {}
) => {
  if (conversationId) {
    const byId = allConversations.findIndex(
      conversation =>
        Number(conversation.id) === Number(conversationId) ||
        Number(conversation.active_conversation_id) === Number(conversationId)
    );
    if (byId > -1) return byId;
  }

  if (contactInboxId) {
    return allConversations.findIndex(
      conversation =>
        Number(conversation.contact_inbox_id) === Number(contactInboxId)
    );
  }

  return -1;
};

export const resolveThreadListRow = (allConversations, conversationId) => {
  const index = findListRowIndexByThread(allConversations, { conversationId });
  if (index > -1) return allConversations[index];
  return allConversations.find(c => c.id === conversationId) || null;
};

// After fetching a conversation by id, the store may have merged it into the
// thread representative row (different display_id, same contact_inbox_id).
export const resolveThreadListRowFromFetched = (
  allConversations,
  conversationId,
  fetchedConversation
) => {
  const listRow = resolveThreadListRow(allConversations, conversationId);
  if (listRow) return listRow;

  const contactInboxId =
    fetchedConversation?.contact_inbox_id ||
    fetchedConversation?.meta?.contact_inbox_id;
  if (!contactInboxId) return null;

  return (
    allConversations.find(
      conversation =>
        Number(conversation.contact_inbox_id) === Number(contactInboxId)
    ) || null
  );
};

export const getListRowByThreadConversationId = (
  allConversations,
  conversationId
) => {
  const index = findListRowIndexByThread(allConversations, { conversationId });
  return index > -1 ? allConversations[index] : null;
};

export const isActiveThreadConversationUpdate = (listRow, conversation) =>
  conversation.id === listRow.active_conversation_id ||
  (!listRow.active_conversation_id && conversation.id === listRow.id);

export const shouldRefreshThreadListRowActivity = (listRow, conversation) => {
  const newActivity = getConversationSortActivityAt(conversation);
  const existingActivity = getConversationSortActivityAt(listRow);

  if (
    listRow.active_conversation_id &&
    conversation.id === listRow.active_conversation_id
  ) {
    return true;
  }

  return newActivity >= existingActivity;
};

export const shouldAdoptThreadListRowId = (
  listRow,
  incoming,
  selectedChatId
) => {
  if (!listRow?.contact_inbox_id || listRow.id === incoming.id) {
    return false;
  }

  if (listRow.id === selectedChatId) {
    return false;
  }

  return shouldRefreshThreadListRowActivity(listRow, incoming);
};

export const hasThreadSeparator = (conversation, conversationId) =>
  (conversation?.thread_separators || []).some(separator => {
    const separatorConversationId =
      separator.conversation_id || separator.conversationId;
    return Number(separatorConversationId) === Number(conversationId);
  });

export const dedupeConversationsByContactInbox = conversations => {
  const deduped = [];
  const contactInboxIndex = {};

  conversations.forEach(conversation => {
    const contactInboxId = conversation.contact_inbox_id;
    if (!contactInboxId) {
      deduped.push(conversation);
      return;
    }

    const existingIndex = contactInboxIndex[contactInboxId];
    if (existingIndex === undefined) {
      contactInboxIndex[contactInboxId] = deduped.length;
      deduped.push(conversation);
      return;
    }

    const existing = deduped[existingIndex];
    if (
      getConversationSortActivityAt(conversation) >=
      getConversationSortActivityAt(existing)
    ) {
      deduped[existingIndex] = conversation;
    }
  });

  return deduped;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    thread_aggregate_status: threadAggregateStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const effectiveStatus = threadAggregateStatus || chatStatus;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(effectiveStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
  unread: ['sortOnUnread', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(
      getConversationSortActivityAt(a),
      getConversationSortActivityAt(b)
    ),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at) ||
    getSortOrderFunction(sortDirection)(a.id, b.id),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    const priorityDiff = getSortOrderFunction(sortDirection)(p1, p2);
    if (priorityDiff !== 0) return priorityDiff;

    return sortDescending(a.last_activity_at, b.last_activity_at);
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return a.created_at - b.created_at;
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortAscending(a.created_at, b.created_at);
      }
      return a.waiting_since ? -1 : 1;
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },

  sortOnUnread: (a, b) => {
    const unreadCountDiff = (b.unread_count || 0) - (a.unread_count || 0);
    if (unreadCountDiff !== 0) return unreadCountDiff;

    return (b.last_activity_at || 0) - (a.last_activity_at || 0);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
